import React, { useEffect, useState } from 'react';
import Button from '../Button';
import Field from '../Field';
import { DELETION_REFUSAL, deletionBlocker, deletionMessage as d } from '../../domain/accountDeletion';
import { familyPortalDeletedPath, familyPortalWord as w } from '../../domain/familyPortal';
import c from '../ConfirmDialog.module.css';
import s from '../settings/DeleteAccountDialog.module.css';
import p from '../../pages/PortalPage.module.css';

// "Delete my login" in the Family Portal (#774).
//
// A family's login (#543) is deleted only on its own request, and that removes
// the login and its memberships; every studio keeps its records. So the
// confirmation says both halves — what goes (the login, and its access to
// every studio and family) and what stays (each studio's records of the
// family) — before anything is typed. MMS has no such control, so the shape is
// the teacher's Delete Account dialog (settings/DeleteAccountDialog.jsx): the
// word, the password, a `danger` button that says what it does, no dismiss on
// the scrim. The word and the password are the server's own requirements
// (core/src/account_deletion.rs: a typed DELETE, and a sign-in minutes old),
// and their sentences are that module's.
//
// It is the teacher's `deleteAccount` callable, told `portalLogin`
// (src/data/accountDeletion.js): the server deletes a portal-only login and
// its memberships, marks each studio's access record `login_deleted`
// (functions/deletion/store.js), and refuses outright for a uid that is also
// a studio. The portal draws this only for a portal-only login
// (`IDENTITY_ROLE.portal`, PortalPage.jsx), so a teacher who is also a parent
// never sees it here; they delete from Business Settings, which takes their
// studio too.
//
// `onDelete` and `onDeleted` are seams for test/ui; by default the deletion
// is the callable, and afterwards the page loads /portal/deleted afresh,
// because the Firestore client has been terminated (accountDeletion.js).
// That module is imported when the button is pressed, not with the portal:
// it brings Firebase Auth's re-authentication and the write queue, which
// nothing else a family opens needs.
const deleteLogin = async ({ password, confirmation }) => {
  const { deleteOwnAccount } = await import('../../data/accountDeletion');
  return deleteOwnAccount({
    password, confirmation, method: 'password', portalLogin: true,
  });
};
const toDeletedPage = () => window.location.replace(familyPortalDeletedPath());

export default function PortalDeleteLogin({ onDelete = deleteLogin, onDeleted = toDeletedPage }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className={p.linkButton} onClick={() => setOpen(true)}>{w('deleteLogin')}</button>
      {open ? <DeleteLoginDialog onDelete={onDelete} onDeleted={onDeleted} onCancel={() => setOpen(false)} /> : null}
    </>
  );
}

function DeleteLoginDialog({ onDelete, onDeleted, onCancel }) {
  const [typed, setTyped] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onCancel]);

  // Online and owed writes are not asked: a family's login writes nothing a
  // queue could owe, and an offline call is refused and said as such.
  const blocker = deletionBlocker({ online: true, owedWrites: 0, typed, passwordTyped: password.length > 0 });
  const passwordRefused = refusal === DELETION_REFUSAL.wrongPassword || refusal === DELETION_REFUSAL.signInTooOld;

  const confirm = async () => {
    if (blocker || busy) return;
    setBusy(true);
    setRefusal(null);
    try {
      await onDelete({ password, confirmation: typed });
    } catch (err) {
      setRefusal(err?.reason || DELETION_REFUSAL.failed);
      if (err?.reason === DELETION_REFUSAL.wrongPassword || err?.reason === DELETION_REFUSAL.signInTooOld) setPassword('');
      setBusy(false);
      return;
    }
    // Left busy: the page is about to be replaced.
    onDeleted();
  };

  return (
    <div className={c.scrim}>
      <div className={c.dialog} role="alertdialog" aria-modal="true" aria-label={w('deleteLoginTitle')}>
        <div className={c.title}>{w('deleteLoginTitle')}</div>
        <div className={c.message}>
          <p className={s.lead}>{w('deleteLoginGoes')}</p>
          <p className={s.para}>{w('deleteLoginStays')}</p>
        </div>
        <form className={s.fields} onSubmit={(e) => { e.preventDefault(); confirm(); }}>
          <Field label={d('confirmLabel')}>
            <input
              type="text"
              value={typed}
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              disabled={busy}
              onChange={(e) => setTyped(e.target.value)}
            />
          </Field>
          <Field label={d('passwordLabel')} hint={d('passwordHint')} error={passwordRefused ? d(refusal) : null}>
            <input
              type="password"
              value={password}
              autoComplete="current-password"
              disabled={busy}
              onChange={(e) => setPassword(e.target.value)}
            />
          </Field>
          <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
        </form>
        {refusal && !passwordRefused ? <p className={s.refusal} role="alert">{d(refusal)}</p> : null}
        <div className={c.foot}>
          <span className={c.spacer} />
          <Button variant="secondary" disabled={busy} onClick={onCancel}>{d('cancel')}</Button>
          <Button variant="danger" loading={busy} disabled={Boolean(blocker) || busy} onClick={confirm}>
            {busy ? w('deleteLoginWorking') : w('deleteLogin')}
          </Button>
        </div>
      </div>
    </div>
  );
}
